import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { ShowProcessResponse } from '@bliks-io/contracts';
import { isForbiddenError, isNotFoundError } from '@/api/errors';
import { processQueryOptions } from '@/api/queries/useProcesses';

/**
 * `beforeLoad` guard of the draft-backed process routes (ADR 0031): redirects
 * a caller who may not edit the process and returns the show-process response
 * otherwise.
 */
export async function requireEditableProcess(
  queryClient: QueryClient,
  params: { slug: string; processId: string }
): Promise<ShowProcessResponse> {
  // show-process answers 404 for a caller who may not READ the process, so
  // an unreadable one has to be caught: unthrown it reaches the root error
  // boundary. The detail page is just as unreadable for that caller, so they
  // go to the list instead. Only the two denial shapes are swallowed — a
  // network drop or a 500 still has to surface, or the user is bounced to a
  // list that cannot load either.
  // fetchQuery revalidates stale or invalidated data: the status the routes
  // gate on changes when another tab publishes.
  const response = await queryClient
    .fetchQuery(processQueryOptions(params.processId))
    .catch((err: unknown) => {
      if (isNotFoundError(err) || isForbiddenError(err)) return null;
      throw err;
    });
  if (!response) {
    throw redirect({ to: '/$slug/processes/all-processes', params });
  }
  if (!response.capabilities.canUpdate) {
    throw redirect({ to: '/$slug/processes/all-processes/$processId', params });
  }
  return response;
}
